import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import i18n from '../_helpers/i18n'
import { ILanguage, ITranslationState } from '../_models/translationInterface'
import type { RootState } from '../_store/store'
import translatedJson from '../translation/translation.json'
import { startLoader, stopLoader } from './loaderSlice'

const DEFAULT_LANGUAGES: ILanguage[] = [
  {
    // eslint-disable-next-line camelcase
    lang_code: 'en',
    language: 'English',
    // eslint-disable-next-line camelcase
    language_flag: '',
  },
]

const initialState: ITranslationState = {
  loader: false,
  translatedData: {
    translations: translatedJson,
    language: 'en',
  },
  languages: DEFAULT_LANGUAGES,
  selectedLanguage: 'en',
}

const getLocalTranslationResponse = () => {
  return {
    status: 'success',
    data: {
      // English is the only bundled translation file
      language: 'en',
      translations: translatedJson,
    },
  }
}

export const translation = createAsyncThunk(
  '/translation',
  async (_language: string | null | undefined, { dispatch }) => {
    dispatch(startLoader())
    try {
      const res = getLocalTranslationResponse()
      const { data } = res

      if (!data?.translations) {
        throw new Error('Local translations are unavailable')
      }

      const lang = data.language
      const translations = data.translations

      // Set i18n resource bundle before returning the payload
      i18n.addResourceBundle(lang, 'translation', translations, true, true)
      await i18n.reloadResources()
      // Save preferred language to sessionStorage
      sessionStorage.setItem('preferredLanguage', lang)
      dispatch(stopLoader())
      return res
    } catch (error) {
      dispatch(stopLoader())
      throw error
    }
  },
)

export const setCommonLanguage = createAsyncThunk(
  '/setCommonLanguage',
  async (language: string) => {
    const { default: translationService } = await import('../_services/translationService')
    const res = await translationService.setCommonLanguage(language)
    return res
  },
)

const translationSlice = createSlice({
  name: 'translation',
  initialState,
  reducers: {
    setLanguage: (state, action) => {
      state.selectedLanguage = action.payload
    },
    resetSelectedLanguage: (state) => {
      state.selectedLanguage = initialState.selectedLanguage
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(translation.pending, (state) => {
        state.loader = true
      })
      .addCase(translation.fulfilled, (state, action) => {
        state.loader = false
        const data = action?.payload?.data
        if (!data?.translations) return

        state.translatedData = data
        state.selectedLanguage = data.language ?? 'en'
        i18n.changeLanguage(state.selectedLanguage)
      })
      .addCase(translation.rejected, (state) => {
        state.loader = false
      })
  },
})
export const { setLanguage, resetSelectedLanguage } = translationSlice.actions
export const translationData = (state: RootState) => ({
  ...state.translation,
  languages: state.translation.languages ?? DEFAULT_LANGUAGES,
})
export default translationSlice.reducer
